'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["_useNativeButton", "aria-pressed", "active", "autoFocus", "block", "buttonRef", "children", "className", "disabled", "external", "href", "iconProps", "onClick", "rel", "responsive", "size", "tabIndex", "target", "truncate", "truncateStringProps", "truncateTooltip", "type", "use"];
import '../listeners/focusStylesListener';
import classNames from 'classnames';
import I18n from 'I18n';
import { Children, useEffect } from 'react';
import { LINK_USES, SIZE_CLASSES, USE_CLASSES } from '../button/ButtonConstants';
import ShareButton from '../decorators/ShareButton';
import useActive from '../hooks/useActive';
import UIIcon from '../icon/UIIcon';
import { setupStyledComponents } from '../listeners/setupStyledComponents';
import UITruncateString from '../text/UITruncateString';
import { getRel, getTarget, renderSecurityTaggedLink, warnIfShouldBeExternal } from '../utils/Links';
import { toShorthandSize } from '../utils/propTypes/tshirtSize';
import { getTabIndex } from '../utils/TabIndex';
import { getSafeUrl } from '../utils/UnsafeUrl';
import UIClickable from './UIClickable';
import { useFoundationsReadinessTracking } from '../utils/useFoundationsReadinessTracking';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
setupStyledComponents();
const getUse = use => use && USE_CLASSES[use] ? use : 'secondary';
export const renderContents = children => {
  let stringBuffer = '';
  let nextChildIndex = 0;
  const childArr = Children.toArray(children);
  return Children.map(children, child => {
    nextChildIndex += 1;
    if (child == null) {
      return null;
    }

    // in response to a breaking change in React 0.15
    // (https://facebook.github.io/react/blog/2016/04/07/react-v15.html#no-more-extra-ltspangts)
    // this wraps any bare strings within a <span> to provide a more
    // predictable DOM for applying automatic spacing rules.
    if (typeof child === 'string') {
      stringBuffer += child;
      if (childArr.length < nextChildIndex || typeof childArr[nextChildIndex] !== 'string') {
        const wrappedStringBuffer = /*#__PURE__*/_jsx("span", {
          children: stringBuffer
        });
        stringBuffer = '';
        return wrappedStringBuffer;
      } else {
        return null;
      }
    }
    return child;
  });
};
const renderExternalButtonIcon = (external, iconProps) => {
  if (!external) return null;

  // Opening links in a new window is an accessibility anti-pattern.
  // To mitigate this, we add accessible text in the icon to warn that
  // the link will be opened in a new window
  // @link http://diveintoaccessibility.info/day_16_not_opening_new_windows.html

  return /*#__PURE__*/_jsx(UIIcon, Object.assign({}, iconProps, {
    "aria-label": I18n.text('salesUI.UILink.externalTitle'),
    className: iconProps && iconProps.className || 'private-link--external__icon',
    name: "externalLink",
    role: "img"
  }));
};
const renderTruncatedContents = (truncate, truncateStringProps, truncateTooltip, renderedContents, renderedExternalButtonIcon, computedUse) => truncate ? /*#__PURE__*/_jsx(UITruncateString, Object.assign({
  innerClassName: "private-button__truncate-string",
  useFlex: true,
  fixedChildren: renderedExternalButtonIcon,
  tooltip: truncateTooltip || renderedContents
}, truncateStringProps, {
  children: /*#__PURE__*/_jsx("span", {
    className: LINK_USES.indexOf(computedUse) === -1 ? 'private-button--non-link' : "",
    children: renderedContents
  })
})) : renderedContents;
const UIButton = props => {
  const {
      _useNativeButton = true,
      'aria-pressed': ariaPressed,
      autoFocus,
      block,
      buttonRef,
      children,
      className,
      disabled = false,
      external = false,
      href,
      iconProps,
      onClick,
      rel,
      responsive = true,
      size = 'md',
      tabIndex,
      target,
      truncate = false,
      truncateStringProps,
      truncateTooltip,
      type = 'button',
      use = 'secondary'
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  useFoundationsReadinessTracking();
  const {
    active,
    onMouseDown,
    onKeyDown,
    onKeyUp,
    onBlur
  } = useActive(props);
  useEffect(() => {
    // Simulate autoFocus when rendering a link element
    if (autoFocus && href) {
      buttonRef.current.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = evt => {
    // Block click events while disabled, since we don't actually use [disabled] in the DOM.
    if (disabled) {
      evt.preventDefault();
      evt.stopPropagation();
    } else {
      if (onClick) onClick(evt);
    }
  };
  const handleKeyDown = evt => {
    if (onKeyDown) onKeyDown(evt);

    // Ignore keydown events that bubbled up
    if (evt.target !== buttonRef.current) return;

    // If this is an <a> with a click handler, simulate click on Spacebar to be more button-like
    if (onClick && href) {
      if (evt.key === ' ') {
        // Prevent scrolling
        evt.preventDefault();
        if (disabled) {
          evt.stopPropagation();
        } else {
          onClick(evt);
        }
      }
    }
  };
  const shorthandSize = toShorthandSize(size);
  const computedTabIndex = getTabIndex(disabled, tabIndex);
  const computedTarget = getTarget(external, target);
  const computedUse = getUse(use);
  if (process.env.NODE_ENV !== 'production') {
    warnIfShouldBeExternal(external, computedTarget, 'UIButton');
  }
  const renderedContents = renderContents(children);
  const renderedExternalButtonIcon = renderExternalButtonIcon(external, iconProps);
  const wrappedContents = renderTruncatedContents(truncate, truncateStringProps, truncateTooltip, renderedContents, renderedExternalButtonIcon, computedUse);
  const unstyled = computedUse === 'unstyled';
  const computedClassName = unstyled ? classNames(className, USE_CLASSES[computedUse]) : classNames('uiButton private-button', USE_CLASSES[computedUse], SIZE_CLASSES[shorthandSize] || SIZE_CLASSES.default, className, active && 'private-button--active', block && 'private-button--block', disabled && 'disabled private-button--disabled', !responsive && 'private-button--non-responsive', LINK_USES.indexOf(computedUse) === -1 && 'private-button--non-link');
  const commonProps = Object.assign({}, rest, {
    'aria-disabled': disabled,
    className: computedClassName,
    'data-button-use': computedUse,
    onBlur,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onKeyUp,
    onMouseDown,
    ref: buttonRef,
    tabIndex: computedTabIndex
  });
  if (typeof href === 'string') {
    const computedRel = getRel(rel, computedTarget);
    return renderSecurityTaggedLink(href, /*#__PURE__*/_jsxs("a", Object.assign({}, commonProps, {
      href: getSafeUrl(href),
      rel: computedRel,
      target: computedTarget,
      children: [wrappedContents, truncate || renderedExternalButtonIcon]
    })));
  }
  const ButtonComponent = _useNativeButton ? 'button' : UIClickable;
  if (!_useNativeButton) {
    // Pass buttonRef down directly to the UIClickable
    // eslint-disable-next-line react-hooks/refs
    commonProps.buttonRef = buttonRef;
    // eslint-disable-next-line react-hooks/refs
    delete commonProps.ref;
  }
  return /*#__PURE__*/_jsx(ButtonComponent, Object.assign({}, commonProps, {
    type: type,
    "aria-pressed": ariaPressed != null ? ariaPressed : active || undefined,
    autoFocus: autoFocus,
    disabled: _useNativeButton ? undefined : disabled,
    onKeyDown: onKeyDown,
    children: wrappedContents
  }));
};
UIButton.displayName = 'UIButton';
export default ShareButton(UIButton);